import {
    ChangeEvent,
    Checkbox, ClickEvent,
    Component,
    DataSourceAction,
    DataSourceExecutionEvent,
    DataSourceMode,
    Layout, LookupModelSelectionEvent, SaveButton,
    SlideoutDecorator, Toast, ValidationResult
} from "@mcleod/components";
import { AutogenLayoutDriverSendMessage } from "./autogen/AutogenLayoutDriverSendMessage";
import { Api, EmailUtil, ModelRow, ModelSearchResult } from "@mcleod/core";
import { PlanningUtil } from "./planning/PlanningUtil";
import { CommonDialogs } from "@mcleod/common";

export interface DriverMessageSlideoutProps {
    movementId: string;
    messageType?: DriverMessageType;
    driverId: string;
    driverName?: string;
    driverEditable: boolean;
    actionSendMessage?: boolean;
}

export enum DriverMessageType {
    SMS = "S",
    EMAIL = "E"
}

export class DriverSendMessage extends AutogenLayoutDriverSendMessage {

    private driverId: string;
    private movementId: string;
    private messageType: DriverMessageType;
    private checkboxPref = {};
    private buttonSend: SaveButton;
    private _messageSent: boolean = false;
    private actionSendMessage: boolean = false;

    public static openSlideout(props: DriverMessageSlideoutProps) {
        const layout = Layout.getLayout("lme/dispatch/DriverSendMessage") as DriverSendMessage;
        const { movementId, messageType, driverId, driverName, driverEditable, actionSendMessage } = props;
        const slideoutDecorator = new SlideoutDecorator({
            layout: layout,
            fillVerticalSpace: true,
            width: "50%",
            title: `Send Driver Email`,
            addlComponents: layout.createHeaderButtons(),
            doAfterSlideIn: () => {
                layout.setup(
                    driverId,
                    movementId,
                    messageType || DriverMessageType.EMAIL,
                    driverEditable,
                    actionSendMessage
                );
                layout.fetchLoadInformation(movementId);
            },
            doAfterSlideOut: () => {
                if (layout._messageSent) Toast.showSuccessToast("Message sent successfully");
            }
        });
    }

    private createHeaderButtons(): Component[] {
        this.buttonSend = SaveButton.create({
            caption: "Send Message",
            rowBreak: false,
            paddingRight: 5,
            onClick: () => this.performSendMessage(false),
            disabledTooltip: "You cannot send this message."
        });
        return [this.buttonSend];
    }

    public async performSendMessage(silent: boolean = false) {
        if (!this.isFormValid()) return;
        if (this.buttonSend) this.buttonSend.busy = true;
        await this.mainDataSource.post().then((row: ModelRow) => {
            if (row && !silent) {
                this._messageSent = true;
                this.slideOut();
            }
        });
        if (this.buttonSend) this.buttonSend.busy = false;
    }

    private isFormValid(): boolean {
        if (!this.textboxDriver.validateSimple(true, true)) {
            CommonDialogs.showError("You must select a Driver to send this message.");
            this.textboxDriver.focus();
            return false;
        }
        if (!this.validateEmailString(this.mainDataSource.activeRow.get("email"))) {
            CommonDialogs.showError("A valid email address (or list of email addresses) is required to send this message.");
            this.textboxEmail.focus();
            return false;
        }
        return true;
    }

    public async fetchLoadInformation(movementId: string) {
        this.panelLoadInformation.busy = true;
        await this.sourceMovementEquipment.search({ id: movementId }).then((result: ModelSearchResult) => {
            if (result.actualRowCount < 1) return;

            this.labelOrderId.addUnderlineWhenMouseOver();
            this.panelLoadInformation.visible = true;
        });
        this.panelLoadInformation.busy = false;
    }

    public setup(driverId: string, movementId: string, messageType: DriverMessageType, driverEditable: boolean = false, actionSendMessage: boolean = false) {
        this.driverId = driverId;
        this.movementId = movementId;
        this.messageType = messageType;
        if (messageType == DriverMessageType.EMAIL) {
            this.panelCharactersUsed.visible = false;
        }
        this.textboxDriver.printable = !driverEditable;

        this.textboxEmail.addlValidationCallback = (value: string, mode: DataSourceMode) => {
            const result:ValidationResult = { component: this.textboxEmail, isValid: this.validateEmailString(value)};
            return result;
        };

        if (actionSendMessage) {
            this.panelEmailFields.fillRow = true;
        }

        this.loadMessageData();
    }

    public loadMessageData() {
        this.search({driver_id: this.driverId, movement_id: this.movementId});
    }

    sourceDriverSendMessageAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            this.loadCheckboxPreferences();
            this.buildMessage(
                this.movementId, this.checkboxStopDetails.checked, this.checkboxArrival.checked,
                this.checkboxContactInfo.checked, this.checkboxComments.checked, this.checkboxReferenceNumbers.checked,
                this.checkboxDirections.checked, this.checkboxDirectionsDisclaimer.checked, this.checkboxDistanceBetwStops.checked,
                false, this.checkboxOfficePhone.checked, this.checkboxCellPhone.checked,
                this.checkboxSenderEmail.checked, this.checkboxDoNotCall.checked
            );
        }
    }

    sourceDriverSendMessageBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.ADD) {
            this.activeRow._appending = true;
            this.activeRow.set({
                movement_id: this.movementId,
                message_type: this.messageType,
                message: this.compileMessage()
            });
        }
    }

    loadCheckboxPreferences() {
        const checkboxPrefString = localStorage.getItem("send_msg_preferences");
        if (checkboxPrefString != null) {
            this.checkboxPref = JSON.parse(checkboxPrefString);
            for (const id in this.checkboxPref) {
                const checkbox = this.findComponentById(id) as Checkbox;
                if (checkbox != null)
                    checkbox.checked = this.checkboxPref[id];
            }
        }
    }

    saveCheckboxPreference(id: string, checked: boolean) {
        this.checkboxPref[id] = checked;
        localStorage.setItem("send_msg_preferences", JSON.stringify(this.checkboxPref));
    }

    checkboxHandler(event: ChangeEvent) {
        const checkbox = (event.target as Checkbox);

        this.buildMessage(
            this.movementId, this.checkboxStopDetails.checked, this.checkboxArrival.checked,
            this.checkboxContactInfo.checked, this.checkboxComments.checked, this.checkboxReferenceNumbers.checked,
            this.checkboxDirections.checked, this.checkboxDirectionsDisclaimer.checked, this.checkboxDistanceBetwStops.checked,
            false, this.checkboxOfficePhone.checked, this.checkboxCellPhone.checked,
            this.checkboxSenderEmail.checked, this.checkboxDoNotCall.checked
        );

        if (event.userInitiatedChange)
            this.saveCheckboxPreference(checkbox.id, event.newValue);
    }

    async buildMessage(movementId: string, includeStopDetails: boolean, includeArrivalTimes: boolean,
                              includeContactInformation: boolean, includeComments: boolean, includeReferenceNumber: boolean,
                              includeDirections: boolean, includeDirectionDisclaimer: boolean, includeDistanceBetweenStops: boolean,
                              includeDistanceBetweenDirections: boolean, includeSendersOfficeNum: boolean, includeSenderCellNum: boolean,
                              includeSendersEmail: boolean, includeDoNotCallNotice: boolean) {
            const response = await Api.search("lme/powerbroker/build-sms-message",
                {
                    "movement_id": movementId,
                    "include_stop_details": includeStopDetails,
                    "include_arrival_times": includeArrivalTimes,
                    "include_contact_info": includeContactInformation,
                    "include_reference_num": includeReferenceNumber,
                    "include_directions": includeDirections,
                    "include_directions_disclaimer": includeDirectionDisclaimer,
                    "include_distance_between_stops": includeDistanceBetweenStops,
                    "include_distance_between_directions": includeDistanceBetweenDirections,
                    "include_comments": includeComments,
                    "include_sender_office_num": includeSendersOfficeNum,
                    "include_sender_cell_num": includeSenderCellNum,
                    "include_sender_email": includeSendersEmail,
                    "include_do_not_call": includeDoNotCallNotice
                });
            if (response.data[0] !== undefined && response.data[0]?.sms_message !== undefined) {
                this.textboxMessage.text = response.data[0].sms_message;
            }
        }

    compileMessage(): string {
        return this.textboxMessage.text;
    }

    textboxMessageOnChange(event: ChangeEvent) {
        const maxCharacters = 160;
        const charactersUsed = this.textboxMessage.text.length;
        let remainCharacters = maxCharacters - charactersUsed;
        let numOfText = Math.ceil(charactersUsed / maxCharacters);

        // If there are more than 1 text messages, reset the remaining characters.
        if (numOfText > 1) {
            numOfText = Math.ceil(charactersUsed / maxCharacters);
            remainCharacters = maxCharacters * numOfText - charactersUsed;
        }

        this.textboxCharactersUsed.text = charactersUsed.toString();
        this.textboxCharactersRemaining.text = remainCharacters.toString();
        this.textboxNumOfTexts.text = numOfText.toString();
    }

    private orderNumberOnClick(event: ClickEvent) {
        PlanningUtil.navigateToOrder(event);
    }

    private async textboxDriverOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (!event.hasSelection()) return;
        this.driverId = event.getValueFromSelectedRow("id");
        this.loadMessageData();
    }

    public validateEmailString(emails: string): boolean {
        const emailsArray = emails.split(/,|;/);
        return emailsArray.every(e => EmailUtil.isValidEmail(e.trim()));
    }
}
